import {
  Database,
  Cache,
  QueryWhere as Where,
  QueryOptions,
  QueryResponse as Response,
} from "../types";
import { firestore } from "firebase-admin";
import { error as logErr, info } from "../../logger";
import { Redis } from "../cache/Redis";
import crypto from "crypto";

export class Firestore implements Database {
  private _db: firestore.Firestore;
  private _cache: Cache;

  constructor(firestore: firestore.Firestore) {
    this._cache = new Redis();
    this._db = firestore;
  }

  async SaveBatch<T>(
    collectionName: string,
    data: (T & { id?: string })[]
  ): Promise<void> {
    const operation = "SaveBatch";
    const timestamp = Date.now();

    try {
      const batch = this._db.batch();
      const result: (T & { id: string })[] = [];

      data.forEach((item) => {
        const id = item.id || this._db.collection(collectionName).doc().id;
        const docRef = this._db.collection(collectionName).doc(id);
        batch.set(docRef, { ...item, modified: timestamp });
        result.push({ ...item, id });
      });

      await batch.commit();

      info(`Batch saved ${result.length} documents to ${collectionName}`, {
        collectionName,
        operation,
      });

      await this._cache.delPattern(`${collectionName}::*`);
    } catch (error) {
      logErr("Could not SaveBatch", {
        collectionName,
        data,
        operation,
        error,
      });
      throw new Error("Could not batch save documents. Try again later.");
    }
  }

  async Save<T>(
    collectionName: string,
    data: { id?: string } & T
  ): Promise<T & { id: string }> {
    const operation = "SaveDocument";
    const timestamp = Date.now();
    try {
      let docId: string;

      if (data?.id) {
        // Atualizando documento existente
        await this._db
          .collection(collectionName)
          .doc(data.id)
          .set({ ...data, modified: timestamp });
        docId = data.id;

        info(`Updating document [${docId}] in collection ${collectionName}.`, {
          operation,
          collectionName,
          documentId: docId,
        });
      } else {
        // Criando um novo documento
        const docRef = await this._db
          .collection(collectionName)
          .add({ ...data, modified: timestamp });
        docId = docRef.id;

        info(
          `Created new document with id ${docId} in collection ${collectionName}.`,
          {
            operation,
            collectionName,
            documentId: docId,
          }
        );
      }

      await this._cache.delPattern(`${collectionName}::*`);
      return { ...data, id: docId };
    } catch (error) {
      logErr(`Could not Save Data in ${collectionName}`, {
        collectionName,
        data,
        operation,
        error,
      });
      throw new Error(`Could not save item in Database [${collectionName}]`);
    }
  }

  async FindOne<T>(collectionName: string, id: string): Promise<T | null> {
    const operation: string = "FindOne";
    try {
      const cacheKey = this.__getCacheKey(collectionName, id);
      const cacheData = await this._cache.get<T>(cacheKey);

      if (cacheData) return cacheData;

      const docRef = this._db.collection(collectionName).doc(id);
      const docSnapshot = await docRef.get();

      if (docSnapshot.exists) {
        const data = docSnapshot.data() as T;
        this._cache.set<T>(cacheKey, data);
        return data;
      }

      return null;
    } catch (error) {
      logErr(`Could not FindOne Data in ${collectionName}`, {
        collectionName,
        id,
        operation,
        error,
      });

      throw new Error("Could not fetch data. Try again later");
    }
  }

  async QueryData<T>(
    collectionName: string,
    query?: QueryOptions<T>,
    noCache?: boolean
  ): Promise<Response<T>> {
    const operation: string = "QueryData";

    try {
      let queryRef: FirebaseFirestore.Query =
        this._db.collection(collectionName);

      if (query?.orderBy) {
        queryRef = queryRef.orderBy(
          query.orderBy.field as string,
          query.orderBy.direction
        );
      }

      query?.wheres?.forEach((where) => {
        queryRef = queryRef.where(
          where.field as string,
          where.operation,
          where.value
        );
      });

      if (query?.cursor) {
        if (!query.orderBy) {
          throw new Error("An orderBy field is required when using cursors");
        }

        const cursorValue = query.cursor || "modified";
        queryRef = queryRef.startAfter(cursorValue);
      }

      const cacheKey = this.__getCacheKey(collectionName, queryRef);
      const cached = await this._cache.get<Response<T>>(cacheKey);

      if (cached && !noCache) return cached;

      const q0 = await queryRef.count().get();
      const count = q0.data().count;

      if (query?.perPage) {
        queryRef = queryRef.limit(query.perPage);
      }

      const querySnapshot = await queryRef.get();
      const res = querySnapshot.docs.map((doc) => doc.data() as T);

      const lastDoc = querySnapshot.docs[querySnapshot.docs.length - 1];

      let cursor: string | number | null = null;
      if (lastDoc && query?.orderBy) {
        cursor = lastDoc.get(query.orderBy.field as string);
      }

      const result: Response<T> = {
        count,
        data: res,
        cursor,
        perPage: query?.perPage,
      };

      if (!noCache) this._cache.set(cacheKey, result);
      return result;
    } catch (error) {
      logErr("Could not Query Data", {
        collectionName,
        error,
        operation,
        query,
      });
      throw new Error("Could not Query your Data. Try again later.");
    }
  }

  async Delete<T>(collectionName: string, query: Where<T>): Promise<boolean> {
    const operation: string = "DeleteData";
    try {
      const idsDeleted: string[] = [];
      const queryResult = this._db
        .collection(collectionName)
        .where(query.field as string, query.operation, query.value);
      const snapshot = await queryResult.get();
      const batch = this._db.batch();

      snapshot.forEach((doc) => {
        batch.delete(doc.ref);
        idsDeleted.push(doc.id);
      });

      await batch.commit();
      info(
        `Deleting data in collection ${collectionName}. Ids deleted: ${idsDeleted}`,
        { collectionName, operation }
      );
      await this._cache.delPattern(`${collectionName}::*`);
      return true;
    } catch (error) {
      logErr(`Could not Delete Data`, {
        collectionName,
        query,
        error,
        operation,
      });
      throw new Error("Error while delete. Try again later");
    }
  }

  async Update<T>(
    collectionName: string,
    id: string,
    data: Partial<T>
  ): Promise<T> {
    const operation: string = "UpdateDocument";
    try {
      const docRef = this._db.collection(collectionName).doc(id);
      await docRef.update({ ...data, modified: Date.now() });
      info(`Updating document ${id} in collection ${collectionName}`, {
        operation,
        documentId: id,
        collectionName,
      });
      await this._cache.delPattern(`${collectionName}::*`); //Clear cache by pattern
      const ref = await docRef.get();
      return ref.data() as T;
    } catch (error) {
      logErr(`Could not Update Data`, {
        collectionName,
        documentId: id,
        data,
        operation,
        error,
      });
      throw new Error("Error while Update data. Try again later.");
    }
  }

  private __getCacheKey = (collectionName: string, data: any) => {
    const hashStr = crypto
      .createHash("md5")
      .update(JSON.stringify(data))
      .digest("hex");
    return `${collectionName}::${hashStr}`;
  };
}
